<template>
  <Teleport v-if="show || animatingOut" to="#page">
    <!-- Need to add show on teleport to be sure that page node mounted -->
    <Transition name="fade">
      <div
        v-if="modalShown"
        :class="['tva-modal-wrapper', { dark }]"
        :style="{ '--overlay-color': overlayColor || 'var(--black-60)' }"
      >
        <div
          class="tva-modal"
          role="dialog"
          ref="modalRef"
          aria-modal="true"
          :aria-labelledby="ariaLabelledbyId"
          @keydown.esc="$emit('onClose', true)"
        >
          <SquareIconButton
            class="tva-modal__close-btn"
            :icon="close"
            black
            @onClick="$emit('onClose')"
          />
          <slot></slot>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { watch } from "vue";
import { ref } from "@vue/reactivity";
import { onMounted, onBeforeUnmount } from "@vue/runtime-core";
import { onClickOutside } from "@vueuse/core";
import SquareIconButton from "./SquareIconButton.vue";

import { close } from "@/assets/icons/icons.js";

const props = defineProps({
  show: Boolean,
  // Id of element that contains popup main title
  ariaLabelledbyId: String,
  overlayColor: String,
  dark: Boolean,
});

const emit = defineEmits(["close"]);
const modalRef = ref(null);
const modalShown = ref(false);
const animatingOut = ref(false);

onClickOutside(modalRef, () => emit("onClose", true));

watch(
  () => props.show,
  (shown) => {
    if (shown) {
      // Freeze page when popup in screen
      document.body.style.overflow = "hidden";
    } else {
      animatingOut.value = true;
      document.body.style.overflow = "initial";
    }

    // Timeout for modal boolean to enable animation
    setTimeout(() => {
      modalShown.value = shown;
    }, 0);

    setTimeout(() => {
      animatingOut.value = false;
    }, 500);
  }
);

watch(
  () => modalRef.value,
  () => focusFirstClickable()
);

onMounted(() => {
  focusFirstClickable();
});

onBeforeUnmount(() => {
  document.body.style.overflow = "initial";
});

function focusFirstClickable() {
  if (modalRef.value) {
    // Set focus to first clickable item (button)
    // NB This should be customized when you use the popup
    const firstClickableItem = modalRef.value.querySelectorAll("button")[0];

    if (firstClickableItem) {
      firstClickableItem.focus();
    }
  }
}
</script>

<style lang="scss" scoped>
.tva-modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: calc(var(--vh) * 100);
  background-color: var(--overlay-color);
  z-index: 10;

  .tva-modal {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    &__close-btn {
      position: absolute;
      top: 1.5rem;
      right: 1.5rem;
      z-index: 2;
      background-color: var(--white-100);

      .mobile &,
      .tablet & {
        right: 50%;
        transform: translateX(50%);
        top: 0.85rem;

        &::before {
          content: "";
          width: 100vw;
          height: 3.2rem;
          position: absolute;
          top: -0.85rem;
          left: calc(-50vw + 50%);
          background-color: var(--white-100);
          z-index: -1;
        }
      }
    }
  }

  &.dark .tva-modal {
    .tva-modal__close-btn {
      right: 50%;
      transform: translateX(50%);
      background-color: transparent;

      &::before {
        background-color: transparent;
      }

      ::v-deep(.svg-icon path) {
        fill: var(--primary-yellow);
      }

      &:hover:not(:disabled) {
        ::v-deep(.svg-icon path) {
          fill: var(--primary-yellow-light);
        }
      }
    }
  }
}
</style>
